import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  formatDays,
  getBalancesOverview,
  getBalancesOverviewTypes,
  type BalancesOverviewRow,
} from '@/lib/absences';
import { formatDate } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { cn } from '@/lib/utils';

type SortKey =
  | 'name'
  | 'available'
  | 'accrued'
  | 'used'
  | 'pending'
  | 'carriedOver'
  | 'nextAccrual';

const COLUMNS: Array<{ key: SortKey; label: string; align: 'left' | 'right' }> = [
  { key: 'name', label: 'Employee', align: 'left' },
  { key: 'available', label: 'Available', align: 'right' },
  { key: 'accrued', label: 'Accrued', align: 'right' },
  { key: 'used', label: 'Used', align: 'right' },
  { key: 'pending', label: 'Pending', align: 'right' },
  { key: 'carriedOver', label: 'Carried over', align: 'right' },
  { key: 'nextAccrual', label: 'Next accrual', align: 'left' },
];

const ALL_DEPARTMENTS = 'all';

/** A row with its name pre-formatted per the company display order (HW-729), so
 * sort/search/render all key off the same value the user actually sees. */
type BalancesRow = BalancesOverviewRow & { displayName: string };

function sortValue(row: BalancesRow, key: SortKey): string | number {
  switch (key) {
    case 'name':
      return row.displayName;
    case 'available':
      return row.balance.available ?? Number.POSITIVE_INFINITY; // unlimited on top when desc
    case 'accrued':
      return row.balance.accrued;
    case 'used':
      return row.balance.used;
    case 'pending':
      return row.balance.pending;
    case 'carriedOver':
      return row.balance.carriedOver;
    case 'nextAccrual':
      return row.accrual?.nextDate ?? '9999-12-31';
  }
}

/** First+last initials, order-independent — the avatar is an identity marker,
 * not the display name, so it doesn't follow the name-display order (HW-729). */
function initials(firstName: string | null, lastName: string | null): string {
  const value = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
  return value || '·';
}

/** Sticky-friendly cell borders: the table uses border-separate so the header
 * and totals rows keep their divider lines while stuck. */
const headerCellClass =
  'sticky top-0 z-10 border-b bg-muted px-3 py-2 font-medium text-muted-foreground';
const footerCellClass =
  'sticky bottom-0 z-10 border-t bg-muted px-3 py-2 font-medium tabular-nums';

export function BalancesOverviewView() {
  const navigate = useNavigate();
  const formatName = useEmployeeNameFormatter();
  const currentYear = new Date().getFullYear();

  const [typeId, setTypeId] = useState<string | null>(null);
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState<string>(ALL_DEPARTMENTS);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'available',
    dir: 'desc',
  });

  const typesQuery = useQuery({
    queryKey: ['balances-overview-types'],
    queryFn: getBalancesOverviewTypes,
  });

  // Default: first accrual-enabled type, else the first active type.
  const effectiveTypeId =
    typeId ??
    typesQuery.data?.find((t) => t.accrualEnabled)?.id ??
    typesQuery.data?.[0]?.id ??
    null;

  const overviewQuery = useQuery({
    queryKey: ['balances-overview', effectiveTypeId, year],
    queryFn: () => getBalancesOverview(effectiveTypeId!, year),
    enabled: !!effectiveTypeId,
  });

  const allRows = overviewQuery.data?.rows ?? [];

  const departments = useMemo(() => {
    const names = new Set<string>();
    for (const r of allRows) if (r.employee.department) names.add(r.employee.department);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // Format the name once per row so search and sort match what the row shows.
    const withName: BalancesRow[] = allRows.map((r) => ({
      ...r,
      displayName: formatName({
        firstName: r.employee.firstName ?? '',
        lastName: r.employee.lastName ?? '',
        preferredName: r.employee.preferredName,
      }),
    }));
    const filtered = withName.filter((r) => {
      if (department !== ALL_DEPARTMENTS && r.employee.department !== department) return false;
      if (!needle) return true;
      const haystack = [r.displayName, r.employee.department, r.employee.position]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (typeof va === 'string' || typeof vb === 'string') {
        return String(va).localeCompare(String(vb)) * dir;
      }
      return (va - vb) * dir;
    });
  }, [allRows, search, department, sort, formatName]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          available: acc.available + (r.balance.available ?? 0),
          accrued: acc.accrued + r.balance.accrued,
          used: acc.used + r.balance.used,
          pending: acc.pending + r.balance.pending,
          carriedOver: acc.carriedOver + r.balance.carriedOver,
        }),
        { available: 0, accrued: 0, used: 0, pending: 0, carriedOver: 0 }
      ),
    [rows]
  );

  const toggleSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
    );

  const isUnlimited = overviewQuery.data?.absenceType.unlimitedBalance ?? false;
  const hasActiveFilters = search.trim() !== '' || department !== ALL_DEPARTMENTS;

  return (
    <div className="px-6 py-4">
      {/* Toolbar: context filters on the left, search + counter on the right */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select value={effectiveTypeId ?? undefined} onValueChange={(v) => setTypeId(v)}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="Absence type" />
          </SelectTrigger>
          <SelectContent>
            {(typesQuery.data ?? []).map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-44">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DEPARTMENTS}>All departments</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex h-9 items-center gap-0.5 rounded-md border border-input px-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-1.5 text-sm font-medium tabular-nums">{year}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-muted-foreground"
            onClick={() => {
              setSearch('');
              setDepartment(ALL_DEPARTMENTS);
            }}
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Reset
          </Button>
        )}

        <div className="ml-auto flex items-center gap-3">
          {overviewQuery.isSuccess && (
            <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
              {hasActiveFilters ? `${rows.length} of ${allRows.length}` : allRows.length} employees
            </span>
          )}
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, department…"
              className="h-9 pl-8"
            />
          </div>
        </div>
      </div>

      {/* States. The overview query is disabled until a type is known, and a
          disabled query reports neither loading nor error — so the types query
          needs its own states or a failed/empty selector renders a blank page. */}
      {(typesQuery.isLoading || overviewQuery.isLoading) && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      )}
      {(typesQuery.isError || overviewQuery.isError) && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {typesQuery.isError
            ? 'Failed to load absence types. Refresh the page to try again.'
            : 'Failed to load balances. Try again or re-select the absence type.'}
        </div>
      )}
      {typesQuery.isSuccess && (typesQuery.data?.length ?? 0) === 0 && (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No active absence types to show balances for.
        </div>
      )}
      {overviewQuery.isSuccess && rows.length === 0 && (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          {hasActiveFilters && allRows.length > 0
            ? 'No employees match your filters.'
            : `No employees are assigned to this type for ${year}.`}
        </div>
      )}

      {/* Table */}
      {overviewQuery.isSuccess && rows.length > 0 && (
        <div className="rounded-md border">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full border-separate border-spacing-0 text-sm">
              <thead>
                <tr className="text-left">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={cn(
                        headerCellClass,
                        'cursor-pointer select-none',
                        col.align === 'right' && 'text-right'
                      )}
                      onClick={() => toggleSort(col.key)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sort.key === col.key &&
                          (sort.dir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          ))}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.employee.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() =>
                      navigate({
                        to: '/employees/$id',
                        params: { id: row.employee.id },
                        search: { tab: 'absences' },
                      })
                    }
                  >
                    <td className="max-w-[320px] border-b px-3 py-2">
                      <span className="flex items-center gap-2.5">
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarImage src={row.employee.avatarUrl ?? undefined} alt="" />
                          <AvatarFallback className="text-[10px] font-medium">
                            {initials(row.employee.firstName, row.employee.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {row.displayName || '—'}
                          </span>
                          {(row.employee.department || row.employee.position) && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {[row.employee.department, row.employee.position]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td
                      className={cn(
                        'border-b px-3 py-2 text-right font-semibold tabular-nums',
                        row.balance.available !== null &&
                          row.balance.available < 0 &&
                          'text-destructive'
                      )}
                    >
                      {isUnlimited || row.balance.available === null
                        ? 'Unlimited'
                        : formatDays(row.balance.available)}
                    </td>
                    <td className="border-b px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatDays(row.balance.accrued)}
                    </td>
                    <td className="border-b px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatDays(row.balance.used)}
                    </td>
                    <td className="border-b px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatDays(row.balance.pending)}
                    </td>
                    <td className="border-b px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {formatDays(row.balance.carriedOver)}
                    </td>
                    <td className="whitespace-nowrap border-b px-3 py-2">
                      {row.accrual?.nextDate ? (
                        <span className="inline-flex items-center gap-2">
                          {formatDate(row.accrual.nextDate, 'short')}
                          <span className="rounded-full border px-2 py-0.5 text-xs tabular-nums">
                            +{formatDays(row.accrual.nextAmount)} d
                          </span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className={cn(footerCellClass, 'text-muted-foreground')}>
                    Total · {rows.length} {rows.length === 1 ? 'employee' : 'employees'}
                  </td>
                  <td className={cn(footerCellClass, 'text-right')}>
                    {isUnlimited ? '—' : formatDays(totals.available)}
                  </td>
                  <td className={cn(footerCellClass, 'text-right text-muted-foreground')}>
                    {formatDays(totals.accrued)}
                  </td>
                  <td className={cn(footerCellClass, 'text-right text-muted-foreground')}>
                    {formatDays(totals.used)}
                  </td>
                  <td className={cn(footerCellClass, 'text-right text-muted-foreground')}>
                    {formatDays(totals.pending)}
                  </td>
                  <td className={cn(footerCellClass, 'text-right text-muted-foreground')}>
                    {formatDays(totals.carriedOver)}
                  </td>
                  <td className={footerCellClass} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
